import { Controller } from 'framework/core';
import { Injectable } from 'framework/di';

import { DTOHelper } from '../../helpers/dto.helper';
import { AppService } from '../app/app.service';
import { QuickViewController } from '../quick-view/quick-view.controller';
import type { QuickViewAction } from '../quick-view/quick-view.model';
import type { ProductDataFromDOM, QuickAddToCartItem } from './product.model';
import { ProductService } from './product.service';
import { ProductSwatchService } from './product-swatch.service';
import { QuickAddToCartController } from './quick-add-to-cart.controller';

@Injectable
export class ProductController extends Controller {
  constructor(
    protected appService: AppService,
    protected productService: ProductService,
    protected swatchService: ProductSwatchService,
    protected quickViewController: QuickViewController,
    protected quickAddToCartController: QuickAddToCartController,
    protected dtoHelper: DTOHelper
  ) {
    super();
  }

  initProductItems(rootElement: Document | HTMLElement): void {
    this.initSwatches(rootElement);
    this.initTooltips(rootElement);
    this.initPriceTransformation(rootElement);
    this.initLocaleUrls(rootElement);
  }

  initSwatches(rootElement: Document | HTMLElement): void {
    const productItems = rootElement.querySelectorAll<HTMLElement>(
      '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
    );
    const swatchSettings = this.getSwatchSettings();

    productItems.forEach((productItem) => {
      this.swatchService.initProductSwatchEvents(productItem);
      this.swatchService.initTooltipScrollHandler(productItem);

      const productData = this.parseProductData(productItem);
      if (productData?.options_with_values) {
        this.swatchService.applySwatchSettings(
          productItem,
          productData.options_with_values,
          swatchSettings
        );
      }
    });
  }

  initTooltips(rootElement: Document | HTMLElement): void {
    const tooltipElements = rootElement.querySelectorAll<HTMLElement>(
      '.boost-sd__product-swatch-option'
    );

    tooltipElements.forEach((element) => {
      element.addEventListener('mouseenter', () => {
        const tooltip = element.querySelector<HTMLElement>('.boost-sd__tooltip');
        if (tooltip) {
          this.positionTooltip(tooltip, element);
        }
      });
    });
  }

  initPriceTransformation(rootElement: Document | HTMLElement): void {
    const blockType = this.detectBlockType(rootElement);
    if (blockType) {
      this.productService.transformProductPrices(rootElement, blockType);
    }
  }

  initLocaleUrls(rootElement: Document | HTMLElement): void {
    this.productService.buildProductUrlsWithLocale(rootElement);
  }

  handleSwatchClick(target: HTMLElement, productItem: HTMLElement): void {
    const swatchOption = target.closest('.boost-sd__product-swatch-option') as HTMLElement | null;
    if (!swatchOption) return;

    const productData = this.parseProductData(productItem);
    const swatchSettings = this.getSwatchSettings();

    this.swatchService.applySwatchSettings(
      productItem,
      productData?.options_with_values,
      swatchSettings
    );
  }

  protected parseProductData(productItem: HTMLElement): ProductDataFromDOM | null {
    const dataAttr = productItem.getAttribute('data-product');
    if (!dataAttr) return null;

    return this.dtoHelper.safeParseJSON<ProductDataFromDOM>(dataAttr);
  }

  protected getSwatchSettings() {
    return this.appService.filterSettings?.swatch_settings;
  }

  handleQuickViewClick(productId: string | number, target: HTMLElement): void {
    const action: QuickViewAction = {
      productId,
      fromSelectOptionBtn: false,
    };

    this.quickViewController.open(action, target);
  }

  handleSelectOptionClick(productId: string | number, target: HTMLElement): void {
    const showQuickView = this.shouldShowQuickView();

    if (showQuickView) {
      const action: QuickViewAction = {
        productId,
        fromSelectOptionBtn: true,
      };
      this.quickViewController.open(action, target);
    } else {
      const item: QuickAddToCartItem = { productId: String(productId) };
      void this.quickAddToCartController.open(item, target);
    }
  }

  handleProductItemClick(target: HTMLElement): void {
    const generalSettings = this.appService.TAEAppConfig.generalSettings;

    this.productService.handleProductItemClick(target, {
      addCollectionToProductUrl: generalSettings?.addCollectionToProductUrl,
    });
  }

  handleAddToCartClick(target: HTMLElement): void {
    const productItem = target.closest('.boost-sd__product-item') as HTMLElement | null;
    if (!productItem) return;

    const productId = productItem.getAttribute('data-product-id');
    if (!productId) return;

    const item: QuickAddToCartItem = { productId };
    void this.quickAddToCartController.open(item, target);
  }

  handleCloseQuickAddToCart(target: HTMLElement): void {
    this.quickAddToCartController.close(target);
  }

  handleClickOutside(target: HTMLElement): void {
    this.quickAddToCartController.handleClickOutside(target);
  }

  protected positionTooltip(tooltip: HTMLElement, anchor: HTMLElement): void {
    const anchorRect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    tooltip.style.left = '';
    tooltip.style.right = '';
    tooltip.style.transform = '';

    if (anchorRect.left + tooltipRect.width / 2 > viewportWidth) {
      tooltip.style.right = '0';
      tooltip.style.left = 'auto';
    } else if (anchorRect.left - tooltipRect.width / 2 < 0) {
      tooltip.style.left = '0';
      tooltip.style.right = 'auto';
    } else {
      tooltip.style.left = '50%';
      tooltip.style.transform = 'translateX(-50%)';
    }
  }

  protected shouldShowQuickView(): boolean {
    const themeSettings = this.appService.themeSettings;
    return themeSettings?.productItems?.ctaButtons?.quickViewOnSelectOption === true;
  }

  protected detectBlockType(
    rootElement: Document | HTMLElement
  ): 'filter' | 'recommendation' | 'search' | null {
    if (rootElement.querySelector('.boost-sd__recommendation')) {
      return 'recommendation';
    }
    if (rootElement.querySelector('.boost-sd__product-list')) {
      return 'filter';
    }
    if (rootElement.querySelector('.boost-sd__search')) {
      return 'search';
    }
    return null;
  }
}
